import path from 'path';

import { ReplaceOperation } from 'fast-json-patch';

import { OffsetPagedResults } from '~interfaces';
import { GeneralPagedParameters } from '~interfaces/requests';
import { IdReferenceResponse, PagedResponse } from '~interfaces/responses';
import { TopologyService } from '~services';

import PaymentStatusEnum from '../enums/paymentStatusEnum';
import RefundStatusEnum from '../enums/refundStatusEnum';
import Payment from '../interfaces/payment';
import { RefundNew } from '../interfaces/refund';

class OneOffPaymentsService extends TopologyService {
	protected readonly path = 'oneoffpayments';
	public readonly refundsSubPath = 'refunds';

	constructor() {
		super('v1');
	}

	async getOneOffPaymentById(oneOffPaymentId: string): Promise<Payment | null> {
		const { data } = await this._client.get<OneOffPaymentResponse>(
			path.join(this.path, oneOffPaymentId)
		);

		return OneOffPaymentsService.fromResponse(data);
	}

	async getOneOffPayments({
		page = 1,
		pageSize = 10,
		descending = false,
		sortBy = 'id',
		...args
	}: GeneralPagedParameters<'id'> & {
		bookingId?: string;
		userId?: string;
		status?: PaymentStatusEnum;
	}): Promise<OffsetPagedResults<Payment>> {
		const { data } = await this._client.get<PagedResponse<OneOffPaymentResponse>>(this.path, {
			params: {
				pageNumber: page,
				pageSize: pageSize,
				bookingId: args.bookingId,
				userId: args.userId,
				status: args.status,
			},
		});

		return this.mapPagedResponse(data, OneOffPaymentsService.fromResponse);
	}

	async cancelOneOffPayment(oneOffPaymentId: string) {
		const content: ReplaceOperation<unknown>[] = [
			{
				op: 'replace',
				path: '/status',
				value: 'canceled',
			},
		];
		await this._client.patch(path.join(this.path, oneOffPaymentId), content);
	}

	async refundOneOffPayment(oneOffPaymentId: string, data: RefundNew): Promise<null> {
		const content = OneOffPaymentsService.toRequest(data);
		await this._client.post(path.join(this.path, oneOffPaymentId, this.refundsSubPath), content);

		return null;
	}

	static fromResponse(data: OneOffPaymentResponse): Payment {
		const {
			id,
			dateCreated,
			status,
			dateModified,
			datePaid,
			user,
			booking,
			refunds,
			panMasked,
			panTruncated,
			reason,
			...rest
		} = data;

		return {
			...rest,
			...OneOffPaymentsService.fromIdReferenceResponse({ id: id }),
			user: OneOffPaymentsService.fromIdReferenceResponse({ id: user.id }),
			booking: OneOffPaymentsService.fromIdReferenceResponse({ id: booking.id }),
			dateCreated: new Date(dateCreated),
			dateModified: new Date(dateModified),
			status: status as PaymentStatusEnum,
			datePaid: datePaid != null ? new Date(datePaid) : undefined,
			primaryAccountNumber:
				panTruncated != null && panMasked != null ?
					{
						truncated: panTruncated,
						masked: panMasked,
					}
				:	undefined,
			refunds: refunds?.map((refund) => ({
				...refund,
				...OneOffPaymentsService.fromIdReferenceResponse({ id: refund.id }),
				dateRefunded: !refund.dateRefunded ? undefined : new Date(refund.dateRefunded),
				status: refund.status as RefundStatusEnum,
				dateCreated: new Date(refund.dateCreated),
				dateModified: new Date(refund.dateModified),
			})),
			refundableTotal: rest.refundableSubtotal * (1 + rest.vatPercentage * 0.01),
			reason: new Set(reason),
		};
	}

	static toRequest(data: RefundNew): RefundRequest {
		const subtotal = data.total / (1 + data.vatPercentage * 0.01);
		const subtotalRounded = Math.round(subtotal * 100) / 100;

		return {
			// TODO: should we let the server calculate this??
			subtotal: subtotalRounded,
		};
	}
}

interface OneOffPaymentResponse extends IdReferenceResponse {
	status: string;
	user: IdReferenceResponse;
	booking: IdReferenceResponse;
	total: number;
	subtotal: number;
	refundableSubtotal: number;
	vatPercentage: number;
	failReason?: string;
	datePaid?: string;
	dateCreated: string;
	dateModified: string;
	panTruncated?: string;
	panMasked?: string;
	paymentMethodType?: string;
	refunds?: RefundResponse[];
	email?: string;
	reason: string[];
}

interface RefundResponse extends IdReferenceResponse {
	dateRefunded?: string;
	subtotal: number;
	total: number;
	status: string;
	dateCreated: string;
	dateModified: string;
}

interface RefundRequest {
	subtotal: number;
}

export default OneOffPaymentsService;
